// client/src/coms/views/canvas_parts/canvas_room_view_parts/calligraphyBrushUtils.js
import { markCanvasDirty } from './canvasUtils';
import { clipContextToSelection } from './selectionUtils';
import { drawGpuEllipseBrushStrokeWithinSelection } from './brushUtils';

export const DEFAULT_CALLIGRAPHY_BRUSH_SHAPE = 'ellipse_calligraphy';

export const CALLIGRAPHY_BRUSH_SHAPES = [
  {
    id: DEFAULT_CALLIGRAPHY_BRUSH_SHAPE,
    label: 'Ellipse calligraphy',
    shortLabel: 'Ellipse',
    description: 'A simple continuous elliptical brush stroke using the normal brush renderer behaviour, with pen/stylus tilt rotation support.',
    angle: 30,
    family: 'calligraphy',
    widthScale: 1,
  },
];

export const DEFAULT_CALLIGRAPHY_SETTINGS = {
  thinning: 0,
  width: 42,
  angle: 30,
  fixation: 0.72,
  usePressure: false,
  useTabletAngle: true,
  caps: true,
  mass: 0,
  drag: 0,
  angleDeposit: 0.35,
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function safeNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function radians(degrees) {
  return (degrees * Math.PI) / 180;
}

function degrees(radianValue) {
  return (radianValue * 180) / Math.PI;
}

const CALLIGRAPHY_TIP_ROTATION_OFFSET = Math.PI * 0.5;

function getPointX(point) {
  return safeNumber(point && (point.x ?? point.clientX ?? point.canvasX), 0);
}

function getPointY(point) {
  return safeNumber(point && (point.y ?? point.clientY ?? point.canvasY), 0);
}

function getPointerType(point) {
  return String(point && point.pointerType ? point.pointerType : 'mouse').toLowerCase();
}

function hasStylusAngle(point) {
  if (getPointerType(point) !== 'pen') return false;

  const altitudeAngle = Number(point && point.altitudeAngle);
  const azimuthAngle = Number(point && point.azimuthAngle);
  if (Number.isFinite(altitudeAngle) && Number.isFinite(azimuthAngle)) return true;

  const tiltX = Number(point && (point.tiltX ?? point.pointerTiltX));
  const tiltY = Number(point && (point.tiltY ?? point.pointerTiltY));
  if (Number.isFinite(tiltX) && Number.isFinite(tiltY) && Math.hypot(tiltX, tiltY) > 0.5) return true;

  const orientation = point && point.pencilOrientation && typeof point.pencilOrientation === 'object' ? point.pencilOrientation : null;
  return !!(orientation && Number.isFinite(Number(orientation.azimuth)));
}

function getTiltMagnitude(point) {
  if (getPointerType(point) !== 'pen') return 0;

  const altitudeAngle = Number(point && point.altitudeAngle);
  if (Number.isFinite(altitudeAngle)) {
    return clamp(1 - (altitudeAngle / (Math.PI * 0.5)), 0, 1);
  }

  const tiltX = Number(point && (point.tiltX ?? point.pointerTiltX));
  const tiltY = Number(point && (point.tiltY ?? point.pointerTiltY));
  if (Number.isFinite(tiltX) && Number.isFinite(tiltY)) {
    return clamp(Math.hypot(tiltX, tiltY) / 90, 0, 1);
  }

  const orientation = point && point.pencilOrientation && typeof point.pencilOrientation === 'object' ? point.pencilOrientation : null;
  const elevation = Number(orientation && orientation.elevation);
  if (Number.isFinite(elevation)) {
    return clamp(1 - (elevation / 90), 0, 1);
  }

  return 0;
}

function getStylusAngle(point, fallbackAngle) {
  if (!hasStylusAngle(point)) return fallbackAngle;

  const altitudeAngle = Number(point && point.altitudeAngle);
  const azimuthAngle = Number(point && point.azimuthAngle);
  if (Number.isFinite(altitudeAngle) && Number.isFinite(azimuthAngle)) {
    return azimuthAngle + Math.PI * 0.5;
  }

  const tiltX = Number(point && (point.tiltX ?? point.pointerTiltX));
  const tiltY = Number(point && (point.tiltY ?? point.pointerTiltY));
  if (Number.isFinite(tiltX) && Number.isFinite(tiltY) && Math.hypot(tiltX, tiltY) > 0.5) {
    return Math.atan2(tiltY, tiltX) + Math.PI * 0.5;
  }

  const orientation = point && point.pencilOrientation && typeof point.pencilOrientation === 'object' ? point.pencilOrientation : null;
  if (orientation && Number.isFinite(Number(orientation.azimuth))) {
    return radians(Number(orientation.azimuth));
  }

  return fallbackAngle;
}

function normalizeCalligraphySettings(options = {}) {
  const stored = options.calligraphySettings && typeof options.calligraphySettings === 'object'
    ? options.calligraphySettings
    : {};

  const size = Math.max(1, safeNumber(
    options.size ?? options.brushSize,
    DEFAULT_CALLIGRAPHY_SETTINGS.width,
  ));
  const width = Math.max(1, safeNumber(
    stored.calligraphyWidth ?? stored.width ?? options.calligraphyWidth ?? options.width,
    DEFAULT_CALLIGRAPHY_SETTINGS.width,
  ));

  return {
    size,
    width,
    angle: clamp(safeNumber(stored.calligraphyAngle ?? stored.angle ?? options.calligraphyAngle ?? options.angle, DEFAULT_CALLIGRAPHY_SETTINGS.angle), -180, 180),
    fixation: clamp(safeNumber(stored.calligraphyFixation ?? stored.fixation ?? options.calligraphyFixation ?? options.fixation, DEFAULT_CALLIGRAPHY_SETTINGS.fixation), 0, 1),
    angleDeposit: clamp(safeNumber(stored.calligraphyAngleDeposit ?? stored.angleDeposit ?? options.calligraphyAngleDeposit ?? options.angleDeposit, DEFAULT_CALLIGRAPHY_SETTINGS.angleDeposit), 0, 1),
    useTabletAngle: stored.calligraphyUseTabletAngle ?? stored.useTabletAngle ?? options.calligraphyUseTabletAngle ?? options.useTabletAngle ?? DEFAULT_CALLIGRAPHY_SETTINGS.useTabletAngle,
    color: options.color || options.strokeColor || options.rgba || '#111111',
    rgba: options.rgba || options.color || options.strokeColor || '#111111',
    opacity: clamp(safeNumber(options.opacity, 1), 0, 1),
    compositeOperation: options.isEraser ? 'destination-out' : (options.compositeOperation || 'source-over'),
  };
}

function getAnglePoint(points) {
  if (!Array.isArray(points)) return null;

  for (let i = points.length - 1; i >= 0; i -= 1) {
    if (hasStylusAngle(points[i])) return points[i];
  }

  return points.length ? points[points.length - 1] : null;
}

function getStrokeAngle(points, settings) {
  const manualAngle = radians(settings.angle);
  if (!settings.useTabletAngle) return manualAngle;

  const point = getAnglePoint(points);
  if (!point || !hasStylusAngle(point)) return manualAngle;

  return getStylusAngle(point, 0) + manualAngle;
}

function getEllipseMetrics(settings, points = null) {
  const baseSize = Math.max(1, safeNumber(settings.size, DEFAULT_CALLIGRAPHY_SETTINGS.width));
  const neutralWidth = Math.max(1, safeNumber(DEFAULT_CALLIGRAPHY_SETTINGS.width, 42));
  const widthAtNeutralSize = Math.max(1, safeNumber(settings.width, neutralWidth));
  const sizeScale = baseSize / neutralWidth;
  const shortRatio = clamp(1 - (settings.fixation * 0.86), 0.14, 1);
  const shortRadius = Math.max(0.5, (neutralWidth / 2) * sizeScale * shortRatio);
  const longRadius = Math.max(shortRadius, (widthAtNeutralSize / 2) * sizeScale);
  const angle = getStrokeAngle(points, settings) + CALLIGRAPHY_TIP_ROTATION_OFFSET;
  const scaleX = Math.max(1, longRadius / shortRadius);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return {
    longRadius,
    shortRadius,
    angle,
    scaleX,
    cos,
    sin,
  };
}

function filterPoints(points) {
  if (!Array.isArray(points)) return [];

  const clean = [];
  points.forEach((point) => {
    if (!point) return;

    const x = getPointX(point);
    const y = getPointY(point);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    const previous = clean[clean.length - 1];
    if (previous && Math.hypot(x - previous.x, y - previous.y) < 0.12) return;

    clean.push({
      ...point,
      x,
      y,
      pointerType: point.pointerType || 'mouse',
      pressure: Number.isFinite(Number(point.pressure)) ? Number(point.pressure) : point.pressure,
      tiltX: Number.isFinite(Number(point.tiltX)) ? Number(point.tiltX) : point.tiltX,
      tiltY: Number.isFinite(Number(point.tiltY)) ? Number(point.tiltY) : point.tiltY,
      pointerTiltX: Number.isFinite(Number(point.pointerTiltX)) ? Number(point.pointerTiltX) : point.pointerTiltX,
      pointerTiltY: Number.isFinite(Number(point.pointerTiltY)) ? Number(point.pointerTiltY) : point.pointerTiltY,
      altitudeAngle: Number.isFinite(Number(point.altitudeAngle)) ? Number(point.altitudeAngle) : point.altitudeAngle,
      azimuthAngle: Number.isFinite(Number(point.azimuthAngle)) ? Number(point.azimuthAngle) : point.azimuthAngle,
      pencilOrientation: point.pencilOrientation || null,
    });
  });

  return clean;
}

function interpolateNumber(a, b, t, fallback = 0) {
  const safeA = safeNumber(a, fallback);
  const safeB = safeNumber(b, safeA);
  return safeA + ((safeB - safeA) * t);
}

function interpolatePoint(a, b, t) {
  const safeT = clamp(Number(t) || 0, 0, 1);
  const ax = getPointX(a);
  const ay = getPointY(a);
  const bx = getPointX(b);
  const by = getPointY(b);

  const point = {
    ...(b || {}),
    x: ax + ((bx - ax) * safeT),
    y: ay + ((by - ay) * safeT),
  };

  const pressureA = Number(a && a.pressure);
  const pressureB = Number(b && b.pressure);
  if (Number.isFinite(pressureA) || Number.isFinite(pressureB)) {
    const safePressureA = Number.isFinite(pressureA) ? pressureA : 1;
    const safePressureB = Number.isFinite(pressureB) ? pressureB : safePressureA;
    point.pressure = safePressureA + ((safePressureB - safePressureA) * safeT);
  }

  const tiltXA = a && (a.tiltX ?? a.pointerTiltX);
  const tiltXB = b && (b.tiltX ?? b.pointerTiltX);
  const tiltYA = a && (a.tiltY ?? a.pointerTiltY);
  const tiltYB = b && (b.tiltY ?? b.pointerTiltY);
  if (Number.isFinite(Number(tiltXA)) || Number.isFinite(Number(tiltXB))) {
    point.tiltX = interpolateNumber(tiltXA, tiltXB, safeT, 0);
  }
  if (Number.isFinite(Number(tiltYA)) || Number.isFinite(Number(tiltYB))) {
    point.tiltY = interpolateNumber(tiltYA, tiltYB, safeT, 0);
  }

  if (Number.isFinite(Number(a && a.altitudeAngle)) || Number.isFinite(Number(b && b.altitudeAngle))) {
    point.altitudeAngle = interpolateNumber(a && a.altitudeAngle, b && b.altitudeAngle, safeT, Math.PI * 0.5);
  }

  if (Number.isFinite(Number(a && a.azimuthAngle)) || Number.isFinite(Number(b && b.azimuthAngle))) {
    point.azimuthAngle = interpolateNumber(a && a.azimuthAngle, b && b.azimuthAngle, safeT, 0);
  }

  if ((a && a.pencilOrientation) || (b && b.pencilOrientation)) {
    point.pencilOrientation = (b && b.pencilOrientation) || (a && a.pencilOrientation);
  }

  if ((a && a.pointerType) || (b && b.pointerType)) {
    point.pointerType = (b && b.pointerType) || (a && a.pointerType);
  }

  return point;
}

function midpointPoint(a, b) {
  if (!a || !b) return null;
  return interpolatePoint(a, b, 0.5);
}

function quadraticPoint(start, control, end, t) {
  const safeT = clamp(Number(t) || 0, 0, 1);
  const startToControl = interpolatePoint(start, control, safeT);
  const controlToEnd = interpolatePoint(control, end, safeT);
  return interpolatePoint(startToControl, controlToEnd, safeT);
}

function getStrokeTravel(points) {
  if (!Array.isArray(points) || points.length <= 1) return 0;

  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }

  return total;
}

function getStrokeSpread(points) {
  if (!Array.isArray(points) || points.length <= 1) return 0;

  const origin = points[0];
  let maxDistance = 0;
  for (let i = 1; i < points.length; i += 1) {
    maxDistance = Math.max(maxDistance, Math.hypot(points[i].x - origin.x, points[i].y - origin.y));
  }

  return maxDistance;
}

function worldToEllipseLocal(point, metrics) {
  const x = getPointX(point);
  const y = getPointY(point);

  return {
    x: ((x * metrics.cos) + (y * metrics.sin)) / metrics.scaleX,
    y: (-x * metrics.sin) + (y * metrics.cos),
  };
}

function moveEllipsePath(ctx, point, metrics) {
  const local = worldToEllipseLocal(point, metrics);
  ctx.moveTo(local.x, local.y);
}

function lineEllipsePath(ctx, point, metrics) {
  const local = worldToEllipseLocal(point, metrics);
  ctx.lineTo(local.x, local.y);
}

function quadraticEllipsePath(ctx, control, end, metrics) {
  const localControl = worldToEllipseLocal(control, metrics);
  const localEnd = worldToEllipseLocal(end, metrics);
  ctx.quadraticCurveTo(localControl.x, localControl.y, localEnd.x, localEnd.y);
}

function drawSingleCalligraphyPoint(ctx, point, settings, options = {}) {
  if (!ctx || !point) return;

  const metrics = getEllipseMetrics(settings, [point]);
  const alphaScale = clamp(safeNumber(options.alphaScale, 1), 0, 1);
  const sizeScale = Math.max(0.15, safeNumber(options.sizeScale, 1));

  ctx.save();
  ctx.globalCompositeOperation = settings.compositeOperation;
  ctx.globalAlpha = settings.opacity * alphaScale;
  ctx.fillStyle = settings.rgba;
  ctx.strokeStyle = settings.rgba;
  ctx.shadowBlur = 0;
  ctx.filter = 'none';
  ctx.translate(point.x, point.y);
  ctx.rotate(metrics.angle);

  ctx.beginPath();
  ctx.ellipse(0, 0, metrics.longRadius * sizeScale, metrics.shortRadius * sizeScale, 0, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}


function getMovementAngle(fromPoint, toPoint) {
  if (!fromPoint || !toPoint) return null;
  const dx = getPointX(toPoint) - getPointX(fromPoint);
  const dy = getPointY(toPoint) - getPointY(fromPoint);
  if (Math.hypot(dx, dy) < 0.5) return null;
  return Math.atan2(dy, dx);
}

function getPointBrushAngle(point, settings, fallbackAngle) {
  if (!point) return fallbackAngle;
  return getStrokeAngle([point], settings) + CALLIGRAPHY_TIP_ROTATION_OFFSET;
}

function getDeterministicScatter(seed, index) {
  const raw = Math.sin((seed * 12.9898) + (index * 78.233)) * 43758.5453;
  return raw - Math.floor(raw);
}

function drawCalligraphyDepositBlob(ctx, point, settings, options = {}) {
  if (!ctx || !point) return;

  const metrics = getEllipseMetrics(settings, [point]);
  const alphaScale = clamp(safeNumber(options.alphaScale, 1), 0, 1);
  const sizeScale = Math.max(0.12, safeNumber(options.sizeScale, 1));
  const roundness = clamp(safeNumber(options.roundness, 0.72), 0.35, 1);
  const rotation = safeNumber(options.rotation, metrics.angle);

  ctx.save();
  ctx.globalCompositeOperation = settings.compositeOperation;
  ctx.globalAlpha = settings.opacity * alphaScale;
  ctx.fillStyle = settings.rgba;
  ctx.strokeStyle = settings.rgba;
  ctx.shadowBlur = 0;
  ctx.filter = 'none';
  ctx.translate(point.x, point.y);
  ctx.rotate(rotation);

  ctx.beginPath();
  ctx.ellipse(
    0,
    0,
    metrics.longRadius * sizeScale,
    Math.max(metrics.shortRadius * sizeScale, metrics.longRadius * sizeScale * roundness),
    0,
    0,
    Math.PI * 2,
  );
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

function drawCalligraphyScatterDeposits(ctx, point, settings, strength = 1, seedAngle = 0) {
  const deposit = clamp(settings.angleDeposit, 0, 1);
  if (!ctx || !point || deposit <= 0) return;

  const safeStrength = clamp(safeNumber(strength, 1), 0, 1);
  const count = Math.max(1, Math.min(8, Math.round(1 + (deposit * 7))));
  const spread = Math.max(0.25, settings.size * (0.025 + (deposit * 0.42)) * (0.65 + (safeStrength * 0.35)));
  const baseAlpha = clamp(0.28 + (deposit * 0.54), 0, 1);
  const seed = ((point.x || 0) * 0.131) + ((point.y || 0) * 0.173) + (seedAngle * 9.31);

  for (let index = 0; index < count; index += 1) {
    const jitterA = getDeterministicScatter(seed, index);
    const jitterB = getDeterministicScatter(seed + 17.7, index);
    const jitterC = getDeterministicScatter(seed + 43.2, index);
    const angle = seedAngle + ((Math.PI * 2 * index) / count) + ((jitterA - 0.5) * 0.86);
    const distance = spread * (0.16 + (jitterB * 0.84));
    const depositPoint = {
      ...point,
      x: point.x + (Math.cos(angle) * distance),
      y: point.y + (Math.sin(angle) * distance),
    };
    const sizeScale = clamp(0.22 + (deposit * 0.36) + (jitterC * 0.22), 0.18, 0.82);
    const alphaScale = clamp(baseAlpha * (0.55 + (getDeterministicScatter(seed + 91.4, index) * 0.38)), 0, 1);
    const roundness = clamp(0.7 + (getDeterministicScatter(seed + 132.8, index) * 0.28), 0.58, 1);

    drawCalligraphyDepositBlob(ctx, depositPoint, settings, {
      alphaScale,
      sizeScale,
      roundness,
      rotation: angle + ((jitterC - 0.5) * 0.7),
    });
  }
}

function getCalligraphyPointBounds(points, metrics, settings) {
  if (!Array.isArray(points) || !points.length || !metrics) return null;

  const deposit = clamp(settings.angleDeposit, 0, 1);
  const depositSpread = deposit > 0 ? Math.max(0.25, settings.size * (0.025 + (deposit * 0.42))) : 0;
  const padding = Math.ceil(Math.max(metrics.longRadius, metrics.shortRadius) + depositSpread + 4);
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;

  points.forEach((point) => {
    if (!point) return;
    const x = getPointX(point);
    const y = getPointY(point);
    left = Math.min(left, x - padding);
    top = Math.min(top, y - padding);
    right = Math.max(right, x + padding);
    bottom = Math.max(bottom, y + padding);
  });

  if (!Number.isFinite(left) || !Number.isFinite(top) || !Number.isFinite(right) || !Number.isFinite(bottom)) return null;
  return { left, top, right, bottom };
}

function clampCalligraphyBoundsToCanvas(bounds, canvas) {
  if (!bounds || !canvas) return null;
  const canvasWidth = Math.max(1, Math.floor(Number(canvas.width) || 1));
  const canvasHeight = Math.max(1, Math.floor(Number(canvas.height) || 1));
  const x = Math.max(0, Math.floor(bounds.left));
  const y = Math.max(0, Math.floor(bounds.top));
  const right = Math.min(canvasWidth, Math.ceil(bounds.right));
  const bottom = Math.min(canvasHeight, Math.ceil(bounds.bottom));
  if (right <= x || bottom <= y) return null;
  return { x, y, width: right - x, height: bottom - y };
}

function sampleCalligraphyStrokePoints(points, metrics, settings) {
  if (!Array.isArray(points) || !points.length) return [];
  if (points.length === 1) return points.slice();

  const spacing = clamp(Math.min(metrics.shortRadius * 0.38, metrics.longRadius * 0.07, settings.size * 0.18), 0.45, 3.25);
  const sampled = [points[0]];
  let previousStampedPoint = points[0];

  for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
    const fromPoint = points[pointIndex - 1];
    const toPoint = points[pointIndex];
    const distance = Math.hypot(getPointX(toPoint) - getPointX(fromPoint), getPointY(toPoint) - getPointY(fromPoint));
    if (!Number.isFinite(distance) || distance <= 0) continue;

    const steps = Math.max(1, Math.ceil(distance / spacing));
    for (let step = 1; step <= steps; step += 1) {
      const point = interpolatePoint(fromPoint, toPoint, step / steps);
      if (previousStampedPoint && Math.hypot(point.x - previousStampedPoint.x, point.y - previousStampedPoint.y) < spacing * 0.38) continue;
      sampled.push(point);
      previousStampedPoint = point;
    }
  }

  return sampled;
}


function drawEllipseCalligraphyStrokeGpu(ctx, points, settings, selection) {
  if (!ctx || typeof ctx.drawEllipseBrushStroke !== 'function' || settings.compositeOperation !== 'source-over') return false;

  const metrics = getEllipseMetrics(settings, points);
  const sampledPoints = sampleCalligraphyStrokePoints(points, metrics, settings);
  if (!sampledPoints.length) return false;

  const didDraw = drawGpuEllipseBrushStrokeWithinSelection(
    ctx,
    sampledPoints,
    {
      size: settings.size,
      rgba: settings.rgba,
      color: settings.color,
      opacity: settings.opacity,
      softness: 0,
      brushPreset: 'calligraphy',
    },
    {
      radiusX: metrics.longRadius,
      radiusY: metrics.shortRadius,
      angle: metrics.angle,
      spacing: clamp(Math.min(metrics.shortRadius * 0.38, metrics.longRadius * 0.07, settings.size * 0.18), 0.45, 3.25),
    },
    selection,
  );

  return didDraw;
}


function getCalligraphyContinuitySpacing(metrics) {
  if (!metrics) return null;

  const thinRatio = metrics.longRadius > 0 ? metrics.shortRadius / metrics.longRadius : 1;
  if (thinRatio >= 0.42 && metrics.shortRadius >= 4) return null;

  return clamp(Math.min(metrics.shortRadius * 0.5, metrics.longRadius * 0.08), 0.55, 2.5);
}

function drawCalligraphyContinuityFill(ctx, points, settings, metrics) {
  if (!ctx || !Array.isArray(points) || points.length <= 1) return;

  const spacing = getCalligraphyContinuitySpacing(metrics);
  if (!spacing) return;

  let previousStampedPoint = null;

  for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
    const fromPoint = points[pointIndex - 1];
    const toPoint = points[pointIndex];
    if (!fromPoint || !toPoint) continue;

    const distance = Math.hypot(getPointX(toPoint) - getPointX(fromPoint), getPointY(toPoint) - getPointY(fromPoint));
    if (!Number.isFinite(distance) || distance <= 0) continue;

    const steps = Math.max(1, Math.ceil(distance / spacing));
    for (let step = 0; step <= steps; step += 1) {
      const t = step / steps;
      const point = interpolatePoint(fromPoint, toPoint, t);
      if (previousStampedPoint && Math.hypot(point.x - previousStampedPoint.x, point.y - previousStampedPoint.y) < spacing * 0.45) continue;

      drawSingleCalligraphyPoint(ctx, point, settings);
      previousStampedPoint = point;
    }
  }
}

function drawEllipseCalligraphyStroke(ctx, points, settings) {
  if (!ctx || !Array.isArray(points) || !points.length) return;

  const metrics = getEllipseMetrics(settings, points);
  const sampledPoints = sampleCalligraphyStrokePoints(points, metrics, settings);
  if (!sampledPoints.length) return;

  ctx.save();
  ctx.globalCompositeOperation = settings.compositeOperation;
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
  ctx.filter = 'none';

  sampledPoints.forEach((point) => {
    drawSingleCalligraphyPoint(ctx, point, settings);
  });

  ctx.restore();

  if (sampledPoints.length === 1) {
    drawCalligraphyScatterDeposits(ctx, sampledPoints[0], settings, 1, getPointBrushAngle(sampledPoints[0], settings, metrics.angle));
  } else if (settings.angleDeposit > 0) {
    const scatterStep = Math.max(1, Math.ceil(sampledPoints.length / 18));
    const pointAngle = getPointBrushAngle(sampledPoints[sampledPoints.length - 1], settings, metrics.angle);
    for (let index = 0; index < sampledPoints.length; index += scatterStep) {
      drawCalligraphyScatterDeposits(ctx, sampledPoints[index], settings, 0.45, pointAngle);
    }
  }

  const canvas = ctx.canvas;
  const bounds = clampCalligraphyBoundsToCanvas(getCalligraphyPointBounds(sampledPoints, metrics, settings), canvas);
  if (bounds) markCanvasDirty(canvas, bounds.x, bounds.y, bounds.width, bounds.height);
}

function buildQuadraticCalligraphyPoints(start, control, end, options = {}) {
  const settings = normalizeCalligraphySettings(options);
  const metrics = getEllipseMetrics(settings, [start, control, end]);
  const approximateLength = Math.hypot(getPointX(control) - getPointX(start), getPointY(control) - getPointY(start))
    + Math.hypot(getPointX(end) - getPointX(control), getPointY(end) - getPointY(control));
  const sampleSpacing = Math.max(0.55, Math.min(7, settings.size * 0.42, metrics.shortRadius * 0.65, metrics.longRadius * 0.08));
  const steps = Math.max(2, Math.min(192, Math.ceil(approximateLength / sampleSpacing)));
  const points = [];

  for (let i = 0; i <= steps; i += 1) {
    points.push(quadraticPoint(start, control, end, i / steps));
  }

  return points;
}

export function drawCalligraphyStrokeWithinSelection(ctx, points, options = {}, selection) {
  if (!ctx) return;

  const cleanPoints = filterPoints(points);
  if (!cleanPoints.length) return;

  const settings = normalizeCalligraphySettings(options);

  if (drawEllipseCalligraphyStrokeGpu(ctx, cleanPoints, settings, selection)) return;

  if (!selection || !selection.bounds) {
    drawEllipseCalligraphyStroke(ctx, cleanPoints, settings);
    return;
  }

  const clipped = clipContextToSelection(ctx, selection);
  drawEllipseCalligraphyStroke(ctx, cleanPoints, settings);
  if (clipped) ctx.restore();
}

export function drawCalligraphySegmentWithinSelection(ctx, fromPoint, toPoint, options = {}, selection, includeStart = false) {
  if (!ctx || !fromPoint) return;

  const points = toPoint
    ? (includeStart ? [fromPoint, toPoint] : [fromPoint, toPoint])
    : [fromPoint];

  drawCalligraphyStrokeWithinSelection(ctx, points, options, selection);
}

export function drawCalligraphyCurveSegmentWithinSelection(ctx, previousPreviousPoint, previousPoint, currentPoint, options = {}, selection) {
  if (!ctx || !previousPreviousPoint || !previousPoint || !currentPoint) return;

  const curveStart = midpointPoint(previousPreviousPoint, previousPoint) || previousPoint;
  const curveEnd = midpointPoint(previousPoint, currentPoint) || currentPoint;
  const points = buildQuadraticCalligraphyPoints(curveStart, previousPoint, curveEnd, options);
  drawCalligraphyStrokeWithinSelection(ctx, points, options, selection);
}

export function drawCalligraphyFinalTailWithinSelection(ctx, previousPoint, currentPoint, options = {}, selection) {
  if (!ctx || !previousPoint || !currentPoint) return;

  const tailStart = midpointPoint(previousPoint, currentPoint) || previousPoint;
  drawCalligraphyStrokeWithinSelection(ctx, [tailStart, currentPoint], options, selection);
}

export function getCalligraphyBrushShape(shapeId) {
  return CALLIGRAPHY_BRUSH_SHAPES.find((shape) => shape.id === shapeId) || CALLIGRAPHY_BRUSH_SHAPES[0];
}

export function getCalligraphyCursorMetrics(size, shapeId = DEFAULT_CALLIGRAPHY_BRUSH_SHAPE, input = null) {
  const inputPoint = input && typeof input === 'object' ? input : null;
  const settings = normalizeCalligraphySettings({
    ...(inputPoint || {}),
    size,
  });
  const metrics = getEllipseMetrics(settings, inputPoint ? [inputPoint] : null);

  return {
    width: Math.max(2, metrics.longRadius * 2),
    height: Math.max(2, metrics.shortRadius * 2),
    rotationDegrees: degrees(metrics.angle),
    pressure: 1,
    pressureContact: 1,
    tiltMagnitude: inputPoint ? getTiltMagnitude(inputPoint) : 0,
  };
}